/* eslint-disable max-lines-per-function */
import Head from 'next/head'
import { CDN_DOMAIN, DOMAIN } from 'src/constants'
import { useRouter } from 'next/router'
import {
  BETA_INDMONEY_HOST,
  BETA_INDSTOCKS_HOST,
  INDGLOBAL_HOST,
  INDMONEY_HOST,
  INDSTOCKS_HOST,
  STENCIL_WARMER_DOMAIN,
} from '@/root/constants/common'

const defaultTitle =
  'INDmoney - Invest in US Stocks, Indian Share market & mutual funds with a free Demat account.'
const defaultIndStocksTitle =
  'Trade Stocks & Options with Flash Trading App & Web by INDstocks | Built for Scalpers, Intraday & Option Traders'
const defaultDesc =
  'Invest in US stocks from India, Indian share market, Direct plan mutual funds, SIP, IPOs, NPS. Track & build your networth with INDmoney.'
const defaultIndStocksDesc =
  'Open your FREE trading account. Experience lightning-fast trades, 1-click scalping, automatic charts, advanced web terminal, and your personal trading assistant. Built for traders who love speed.'

const getdefaultSchemas = (
  isIndStocks = false,
  title = defaultTitle,
  description = defaultDesc,
  canonicalUrl,
  removeOrganizationSchema = false,
) =>
  isIndStocks
    ? [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'INDstocks',
        description: defaultIndStocksDesc,
        url: 'https://www.indstocks.com/',
        logo: 'https://indcdn.indmoney.com/public/images/256x256.png',
        email: 'support@indmoney.com(opens in new tab)',
        sameAs: [
          'https://www.facebook.com/INDmoney/',
          'https://x.com/INDmoneyApp/',
          'https://www.linkedin.com/company/indmoney',
          'https://www.instagram.com/indmoneyapp/',
          'https://www.youtube.com/channel/UCm7xZ9sbqhuARuDDcckh8Jg',
          'https://t.me/indmoneyofficial',
          'https://www.reddit.com/r/INDmoneyApp/',
          'https://www.quora.com/profile/INDmoney-3/',
          'https://www.whatsapp.com/channel/0029VaDZ1NNDjiOWs9WfYa2E',
        ],
      },
    ]
    : [
      ...(removeOrganizationSchema
        ? []
        : [
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'INDmoney',
            description: defaultDesc,
            url: 'https://www.indmoney.com/',
            logo: 'https://indcdn.indmoney.com/public/images/256x256.png',
            foundingDate: '2019',
            founder: [
              {
                '@type': 'Person',
                name: ' Ashish Kashyap - Founder ',
              },
            ],
            address: {
              '@type': 'PostalAddress',
              streetAddress:
                '616, 6th Floor Suncity Success Tower, Sector 65',
              addressLocality: 'Gurugram',
              addressRegion: 'Haryana',
              addressCountry: 'IN',
              postalCode: '122005',
            },
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '07314852049',
              contactType: 'Customer Care ',
              areaServed: 'India',
            },

            sameAs: [
              'https://www.facebook.com/INDmoney/',
              'https://x.com/INDmoneyApp/',
              'https://www.linkedin.com/company/indmoney',
              'https://www.instagram.com/indmoneyapp/',
              'https://www.youtube.com/channel/UCm7xZ9sbqhuARuDDcckh8Jg',
              'https://t.me/indmoneyofficial',
              'https://www.reddit.com/r/INDmoneyApp/',
              'https://www.quora.com/profile/INDmoney-3/',
              'https://www.whatsapp.com/channel/0029VaDZ1NNDjiOWs9WfYa2E',
            ],
          },
        ]),
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: canonicalUrl,
        publisher: {
          '@type': 'Organization',
          name: 'INDmoney',
          url: DOMAIN,
          logo: {
            '@type': 'ImageObject',
            url: 'https://indcdn.indmoney.com/public/images/256x256.png',
          },
        },
      },
    ]
export interface SEOProps {
  data?: {
    title?: string
    description?: string
    keywords?: string
    canonicalUrl?: string | null
    index?: boolean
    revisedDetail?: string
    image?: string
    twitterImage?: string
    schemas?: Array<unknown> | null
    maxImagePreview?: 'large'
    noIndexNoFollow?: boolean
  }
  noIndex?: boolean
  host?: string
  noIndexNoFollow?: boolean
  noSchema?: boolean
  removeOrganizationSchema?: boolean
}
// eslint-disable-next-line complexity
const SEOHeader = ({
  data,
  noIndex = false,
  host,
  noIndexNoFollow = false,
  noSchema = false,
  removeOrganizationSchema = false,
}: SEOProps) => {
  const {
    title = host === INDSTOCKS_HOST ? defaultIndStocksTitle : defaultTitle,
    description = host === INDSTOCKS_HOST ? defaultIndStocksDesc : defaultDesc,
    // keywords,
    canonicalUrl,
    index,
    revisedDetail,
    image,
    twitterImage,
    schemas = [],
    // maxImagePreview,
  } = data ?? {}

  const indStocksIndexedPages = ['/', '/features*']
  const noIndexPages = ['/page*', '/invest*', '/dashboard']

  const { pathname, asPath } = useRouter()

  const finalCanonicalUrl =
    canonicalUrl ??
    (host && [INDMONEY_HOST, STENCIL_WARMER_DOMAIN].includes(host)
      ? `${DOMAIN}${asPath.split('?')[0]}`
      : null)

  const getIndexedPagesRegex = (pages: string[]) => {
    return pages.some(pagePath => {
      // Replace '*' with '.*' to properly create a regex pattern
      const regexPattern = `^${pagePath.replace('*', '.*')}$`
      const regex = new RegExp(regexPattern)
      return regex.test(pathname)
    })
  }

  const isIndStocksIndexed = getIndexedPagesRegex(indStocksIndexedPages)
  const isNoIndexPages = getIndexedPagesRegex(noIndexPages)

  return (
    <Head>
      <title>{title}</title>
      <meta name="title" content={title} key="title" />
      <meta name="description" content={description} key="description" />
      {finalCanonicalUrl && (
        <link rel="canonical" href={finalCanonicalUrl} key="canonical" />
      )}
      {/* {keywords && <meta name="keywords" content={keywords} key="keywords" />} */}
      {revisedDetail && (
        <meta
          name="revised detail"
          content={revisedDetail}
          key="reviseddetail"
        />
      )}

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex" key="noindex" />}

      {!(noIndex || !!noIndexNoFollow || index) &&
        host &&
        [INDMONEY_HOST, STENCIL_WARMER_DOMAIN].includes(host) && (
          <meta
            name="robots"
            content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1"
          />
        )}

      {!!(
        (host === INDSTOCKS_HOST && !isIndStocksIndexed) ||
        (host &&
          [INDGLOBAL_HOST, BETA_INDMONEY_HOST, BETA_INDSTOCKS_HOST].includes(
            host,
          )) ||
        noIndexNoFollow ||
        isNoIndexPages
      ) && <meta name="robots" content="noindex,nofollow" />}
      {index && (
        <meta name="robots" content="index, follow" key="index, follow" />
      )}

      {!!(host === INDSTOCKS_HOST) && (
        <meta
          name="google-site-verification"
          content="pgbZqpGZQ9cNz8Xc2atunt8iqf4DDvfGdzc40Nkxtpg"
        />
      )}
      {host &&
        [INDSTOCKS_HOST, INDGLOBAL_HOST, BETA_INDMONEY_HOST].includes(host) && (
          <meta
            name="google-site-verification"
            content="bK911LSyXhys607FZbAbdQNCO633pqyl3VgKNQ59yg8"
          />
        )}

      {/* OG tags */}
      <meta property="og:title" content={title} key="og:title" />
      <meta
        property="og:description"
        content={description}
        key="og:description"
      />
      <meta property="og:type" content="website" key="website" />
      <meta property="og:locale" content="en_US" key="en_US" />
      <meta property="og:site_name" content="INDmoney" key="INDmoney" />
      <meta
        property="og:image"
        content={image ?? `${CDN_DOMAIN}/public/images/256x256.png`}
        key="og:image"
      />
      {image && (
        <meta
          property="og:image:alt"
          content={description}
          key="og:image:alt"
        />
      )}
      {finalCanonicalUrl && (
        <meta property="og:url" content={finalCanonicalUrl} key="og:url" />
      )}
      <meta
        name="twitter:image"
        content={
          twitterImage ?? image ?? `${CDN_DOMAIN}/public/images/256x256.png`
        }
        key="twitter:image"
      />
      <meta
        name="twitter:card"
        content="summary_large_image"
        key="twitter:card"
      />
      <meta
        name="twitter:image:alt"
        content="INDmoney Logo"
        key="twitter:image:alt"
      />
      <meta name="twitter:title" content={title} key="twitter:title" />
      <meta
        name="twitter:description"
        content={description}
        key="twitter:description"
      />
      <meta
        name="twitter:creator"
        content="@indmoneyapp"
        key="twitter:creator"
      />
      <meta name="twitter:site" content="@indmoneyapp" key="twitter:site" />

      {/* The `noSchema` flag controls whether custom schemas are shown. */}
      {/* Without this check, `<SEOHeader />` would render twice on pages with custom schemas—once in the page component and again in the root `_app.tsx`, resulting in duplicate default schemas. */}

      {!noSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@graph': [
                ...getdefaultSchemas(
                  host === INDSTOCKS_HOST,
                  title,
                  description,
                  finalCanonicalUrl,
                  removeOrganizationSchema,
                ),
                ...(schemas ?? []),
              ],
            }),
          }}
        />
      )}
    </Head>
  )
}

export default SEOHeader
